import React, { createContext, useContext, useState, ReactNode } from 'react';

export type ThemeType = 'default' | 'green' | 'bubbles' | 'raindrops' | 'snow' | 'floating';

interface ThemeContextType {
  currentTheme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  isParticlesActive: boolean;
  toggleParticles: () => void;
  getThemeColors: () => ThemeColors;
}

interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  gradient: string;
  gradientHover: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export type ColorMode = 'system' | 'light' | 'dark';

const themeConfigs: Record<ThemeType, ThemeColors> = {
  default: {
    primary: 'from-purple-500 to-pink-600',
    secondary: 'from-purple-600 to-pink-700',
    accent: 'from-indigo-500 to-purple-600',
    background: 'from-gray-900 via-purple-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-purple-500 to-pink-600',
    gradientHover: 'from-purple-600 to-pink-700'
  },
  green: {
    primary: 'from-emerald-500 to-green-600',
    secondary: 'from-emerald-600 to-green-700',
    accent: 'from-teal-500 to-emerald-600',
    background: 'from-gray-900 via-emerald-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-emerald-500 to-green-600',
    gradientHover: 'from-emerald-600 to-green-700'
  },
  bubbles: {
    primary: 'from-cyan-500 to-blue-600',
    secondary: 'from-cyan-600 to-blue-700',
    accent: 'from-sky-500 to-cyan-600',
    background: 'from-gray-900 via-cyan-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-cyan-500 to-blue-600',
    gradientHover: 'from-cyan-600 to-blue-700'
  },
  raindrops: {
    primary: 'from-blue-500 to-indigo-600',
    secondary: 'from-blue-600 to-indigo-700',
    accent: 'from-slate-500 to-blue-600',
    background: 'from-gray-900 via-blue-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-blue-500 to-indigo-600',
    gradientHover: 'from-blue-600 to-indigo-700'
  },
  snow: {
    primary: 'from-slate-500 to-gray-600',
    secondary: 'from-slate-600 to-gray-700',
    accent: 'from-gray-500 to-slate-600',
    background: 'from-gray-900 via-slate-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-slate-500 to-gray-600',
    gradientHover: 'from-slate-600 to-gray-700'
  },
  floating: {
    primary: 'from-purple-500 to-pink-600',
    secondary: 'from-purple-600 to-pink-700',
    accent: 'from-violet-500 to-purple-600',
    background: 'from-gray-900 via-purple-900 to-gray-900',
    surface: 'bg-gray-800/90',
    text: 'text-white',
    textSecondary: 'text-gray-300',
    border: 'border-gray-700/50',
    gradient: 'from-purple-500 to-pink-600',
    gradientHover: 'from-purple-600 to-pink-700'
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeType>('default');
  const [isParticlesActive, setIsParticlesActive] = useState(true);
  const [colorMode, setColorModeState] = useState<ColorMode>('system');

  const applyDocumentMode = (mode: ColorMode) => {
    const root = document.documentElement;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDark = mode === 'dark' || (mode === 'system' && prefersDark);
    root.classList.toggle('dark', isDark);
  };

  const setTheme = (theme: ThemeType) => {
    setCurrentTheme(theme);
    localStorage.setItem('sage-theme', theme);
  };

  const setColorMode = (mode: ColorMode) => {
    setColorModeState(mode);
    localStorage.setItem('sage-color-mode', mode);
    applyDocumentMode(mode);
  };

  const toggleParticles = () => {
    setIsParticlesActive(!isParticlesActive);
    localStorage.setItem('sage-particles', (!isParticlesActive).toString());
  };

  const getThemeColors = () => {
    const base = themeConfigs[currentTheme];
    const isDark = document.documentElement.classList.contains('dark');
    if (isDark) return base;
    // Light mode overrides for a true white look
    return {
      ...base,
      background: 'from-white to-slate-50',
      surface: 'bg-white/90',
      text: 'text-slate-900',
      textSecondary: 'text-slate-600',
      border: 'border-slate-200',
    };
  };

  // Load saved theme on mount
  React.useEffect(() => {
      const savedTheme = localStorage.getItem('sage-theme') as ThemeType;
  const savedParticles = localStorage.getItem('sage-particles');
  const savedColorMode = (localStorage.getItem('sage-color-mode') as ColorMode) || 'system';
    
    if (savedTheme && themeConfigs[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
    
    if (savedParticles !== null) {
      setIsParticlesActive(savedParticles === 'true');
    }

    setColorModeState(savedColorMode);
    // Apply immediately and listen for system changes when in system mode
    applyDocumentMode(savedColorMode);
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const mode = (localStorage.getItem('sage-color-mode') as ColorMode) || 'system';
      if (mode === 'system') applyDocumentMode(mode);
    };
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else (mql as any).addListener?.(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else (mql as any).removeListener?.(handler);
    };
  }, []);

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    colorMode,
    setColorMode,
    isParticlesActive,
    toggleParticles,
    getThemeColors
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
